import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ApiRequestError, apiRequestJson, publicRequestJson } from '../api';
import { useAuthOptional } from '../auth/AuthContext';
import { resolveAuthProviderName } from '../auth/authProvider';
import {
  useAuthProviderName,
  useLoginProviderName,
} from '../auth/useAuthProviderName';
import AuthPageShell from './AuthPageShell';

type InviteStatus = {
  email: string | null;
  actionType: string;
  expiresAt: string;
  // Absent on responses from a backend older than the auto-resend feature;
  // treat missing as valid so a deploy-skew window can't lock the form.
  state?: 'valid' | 'expired';
  resent?: boolean;
};

const expiredMessage = (invite: InviteStatus | null): string => {
  if (invite?.actionType === 'password_reset') {
    return 'This link has expired. Please request a new one from the sign-in page.';
  }
  if (invite?.resent) {
    return 'This link has expired. We just emailed you a new one — please check your inbox.';
  }
  return 'This link has expired. Please check your email for a new link.';
};

// ───────────────────────────────────────────────────────────────────────────
// Deep-link stash (US-009, Scalekit only)
//
// The passwordless invite flow crosses the signed-out → signed-in boundary
// mid-flight: `POST /api/auth/invites/redeem` is `@Authenticated`, so a visitor
// arriving on `/accept-invite?token=…` has to go to the hosted login and come
// BACK before the token can be spent.
//
// The token must not ride along in the OAuth `redirect_uri`: an authorize URL
// is logged by the identity provider and kept in browser history, and an invite
// token has no business in either. So the return path handed to the provider is
// the CLEAN `/accept-invite`, and the token waits here, in the tab that started
// the flow.
//
// Handing `INVITE_RETURN_PATH` to the explicit sign-in click is NOT sufficient
// on its own, which is why the token is also taken OUT OF THE ADDRESS BAR the
// moment it is stashed. Two reasons, and the second is the one a code review
// found:
//
//   1. While `?token=…` is in `window.location` it is in this tab's history
//      entry and in the `Referer` of everything the page loads from then on.
//   2. `buildRedirectUri()` with no argument reproduces the CURRENT location,
//      query string included — deliberately, because `token` is excluded from
//      `OAUTH_QUERY_PARAMS`. Under KEYCLOAK that capture IS the `redirect_uri`,
//      so a visitor sitting on `/accept-invite?token=…` whose silent renewal
//      fails — routine when third-party cookies are blocked — would otherwise
//      send the token to the authorize endpoint after all.
//
//      Under Scalekit that particular exposure is closed twice over: the
//      `redirect_uri` is the fixed, registered `/auth/callback` (Scalekit
//      matches callbacks by exact string, so it could not be anything else),
//      and the captured location travels in the OIDC `state`, which lives in
//      this tab's storage and never appears on the authorize URL. The strip
//      still earns its place for reason 1 and for the Keycloak path, which is
//      the one shipping today.
//
// WHAT THE STRIP DOES NOT BUY. An earlier version of this comment — and
// a36271a28's commit message — said the token "stays out of authorize URLs, IdP
// logs, browser history and Referer headers". The last two halves overreach,
// and the precise statement matters more than the tidy one. The strip runs in a
// React effect, i.e. necessarily AFTER the browser requested the document at
// `/accept-invite?token=…`, so it cannot:
//
//   - remove the token from that FIRST request URL, which the server serving
//     the page (plus any CDN, proxy or access log in front of it) already has;
//   - remove it from the `Referer` of any subresource requested before the
//     effect ran.
//
// `replaceState` bounds LATER exposure; it cannot unsend a request. Those two
// residues are properties of delivering an invite as a query parameter at all,
// and only a different link shape could remove them — a delivery-format change,
// not something this page can do. What the strip genuinely buys is everything
// downstream of it: no authorize URL ever carries the token, so the identity
// provider never logs it; the history entry is REPLACED rather than pushed, so
// the dirty URL does not survive in the tab; and every later `Referer` is
// clean.
//
// The residue is bounded by design rather than merely tolerated. Post-US-009
// the token is not a credential: redemption is authenticated and
// principal-bound, so somebody who reads one out of a log learns the invitee's
// email from the anonymous GET and can trigger one guarded auto-resend. That is
// disclosure, not takeover.
//
// Stripping is conditional on the stash having been CONFIRMED (see
// `stashToken`). There is exactly one copy of this token in the tab; a browser
// that refuses storage must lose the stash, never the invite — and must be TOLD
// before it spends a hosted round trip finding out (see `handleSignIn`).
//
// sessionStorage, not localStorage: the stash is scoped to one tab and dies
// with it. Every access is guarded — privacy modes and embedded webviews throw
// on access rather than returning null, and a blocked storage API must degrade
// to "no stash", never to a crashed page. Same guard pattern as
// `createRecoveryBudget` in `src/auth/browserSession.ts`.
// ───────────────────────────────────────────────────────────────────────────

export const INVITE_PENDING_TOKEN_KEY = 'lutiq.invite.pendingToken';

/**
 * Where the identity provider sends the browser back to. CLEAN — no query
 * string. Changing this to carry the token is the one edit this whole mechanism
 * exists to prevent.
 */
export const INVITE_RETURN_PATH = '/accept-invite';

const readStashedToken = (): string => {
  try {
    return sessionStorage.getItem(INVITE_PENDING_TOKEN_KEY) ?? '';
  } catch {
    return '';
  }
};

/**
 * Best-effort stash.
 *
 * @returns `true` only when the token was written AND reads back — i.e. when
 *   the stash is a real second copy. That answer is load-bearing twice over: it
 *   is the precondition for removing the token from the address bar below, so a
 *   browser whose storage is blocked (or which accepts `setItem` and then
 *   returns null, as private-mode Safari historically did) keeps the URL copy
 *   rather than losing the invite entirely; and it is the precondition for
 *   STARTING the hosted round trip at all (see `handleSignIn`).
 */
const stashToken = (token: string): boolean => {
  try {
    sessionStorage.setItem(INVITE_PENDING_TOKEN_KEY, token);
    return sessionStorage.getItem(INVITE_PENDING_TOKEN_KEY) === token;
  } catch {
    // Storage blocked. Not fatal here — throwing on a click would be — and the
    // URL copy survives, so the invite is still redeemable in this page load.
    return false;
  }
};

const clearStashedToken = (): void => {
  try {
    sessionStorage.removeItem(INVITE_PENDING_TOKEN_KEY);
  } catch {
    // ignore
  }
};

// ───────────────────────────────────────────────────────────────────────────
// Keycloak branch — UNCHANGED
//
// Behaviour, copy and DOM are exactly what shipped before US-009, because the
// rollback lever (US-006) has to restore a working password flow by flipping
// config, not by reverting code. The only edit is the shell extraction into
// `AuthPageShell`, which emits the same markup.
// ───────────────────────────────────────────────────────────────────────────

const KeycloakAcceptInvite = () => {
  const [searchParams] = useSearchParams();
  // Same `URL first, stash second` rule as the Scalekit branch, and for one
  // narrow but real case: in the build-flag-scalekit / backend-keycloak skew
  // window the Scalekit branch renders FIRST (the build flag is synchronous,
  // `auth_provider` is not), strips the token from the URL, and only then hands
  // over. Without this fallback that hand-over arrives with no token at all and
  // the invitee is told their link is missing. On a plain Keycloak deployment
  // nothing ever writes the stash, so this reads `''` exactly as before.
  const urlToken = searchParams.get('token');
  const token = useMemo(() => urlToken ?? readStashedToken(), [urlToken]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error' | 'expired'>('idle');
  const [invite, setInvite] = useState<InviteStatus | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Invite token is missing.');
      return;
    }
    setStatus('loading');
    void publicRequestJson<InviteStatus>(`/api/auth/invites/${encodeURIComponent(token)}`)
      .then((payload) => {
        setInvite(payload);
        setStatus(payload.state === 'expired' ? 'expired' : 'idle');
      })
      .catch((error) => {
        setStatus('error');
        setMessage(error instanceof Error ? error.message : 'Invite is invalid.');
      });
  }, [token]);

  const submitLabel = invite?.actionType === 'password_reset' ? 'Reset password' : 'Set password';
  const requiresName = invite?.actionType !== 'password_reset';

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus('loading');
    setMessage(null);
    try {
      await publicRequestJson('/api/auth/invites/accept', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          password,
          ...(requiresName ? { firstName, lastName } : {}),
        }),
      });
      setStatus('success');
    } catch (error) {
      // 410 Gone = the link expired between page load and submit. The
      // backend has already fired its guarded auto-resend, so show the
      // same check-your-email guidance instead of a raw error.
      if (error instanceof ApiRequestError && error.status === 410) {
        setStatus('expired');
        return;
      }
      setStatus('error');
      setMessage(error instanceof Error ? error.message : 'Unable to complete invite.');
    }
  };

  return (
    <AuthPageShell
      eyebrow="Account setup"
      title={invite?.actionType === 'password_reset' ? 'Reset your password' : 'Activate your account'}
      subtitle={invite?.email ? `Continue as ${invite.email}` : undefined}
    >
      {status === 'loading' ? <LinearProgress /> : null}
      {message ? <Alert severity="error">{message}</Alert> : null}
      {status === 'expired' ? (
        <Alert severity="warning">{expiredMessage(invite)}</Alert>
      ) : null}
      {status === 'success' ? (
        <Alert severity="success">
          Password updated. You can now return to the sign-in page.
        </Alert>
      ) : null}

      {status !== 'success' && status !== 'expired' ? (
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Stack spacing={2}>
            {requiresName ? (
              <>
                <TextField
                  label="First name"
                  fullWidth
                  value={firstName}
                  onChange={(event) => setFirstName(event.target.value)}
                  disabled={!invite || status === 'loading'}
                />
                <TextField
                  label="Last name"
                  fullWidth
                  value={lastName}
                  onChange={(event) => setLastName(event.target.value)}
                  disabled={!invite || status === 'loading'}
                />
              </>
            ) : null}
            <TextField
              label="New password"
              type="password"
              fullWidth
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              disabled={!invite || status === 'loading'}
            />
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={
                !invite
                || status === 'loading'
                || password.trim().length < 8
                || (requiresName
                  && (firstName.trim().length === 0 || lastName.trim().length === 0))
              }
            >
              {submitLabel}
            </Button>
          </Stack>
        </Box>
      ) : null}

      <Button variant="text" onClick={() => { window.location.href = '/'; }}>
        Back to sign in
      </Button>
    </AuthPageShell>
  );
};

// ───────────────────────────────────────────────────────────────────────────
// Scalekit branch — passwordless
//
// Three states, in the order a real invitee meets them:
//
//   1. Signed out → "Sign in to accept". No password field exists anywhere on
//      this branch, and no length gate, because there is no password.
//   2. Signed in  → collect a name if the invite asks for one, then
//      `POST /api/auth/invites/redeem` as the caller.
//   3. Expired    → one sentence that never mentions choosing a password.
//
// The redemption endpoint answers a refusal with ONE status and ONE string for
// every cause — unknown, blank, expired, consumed, wrong principal, unmapped
// caller. That is deliberate on the server (see `PasswordFlowPolicy`), so this
// component renders the message it was given and never branches on `details`.
// A UI that said "this invite belongs to someone else" would rebuild the
// enumeration oracle the backend just closed.
// ───────────────────────────────────────────────────────────────────────────

const SCALEKIT_EXPIRED_MESSAGE =
  'This invite link has expired. Ask an admin to send you a new one.';

/**
 * Shown INSTEAD of starting the hosted round trip when this browser cannot hold
 * the token across it. Names the two things the visitor can actually do; it
 * does not mention a credential, because there isn't one.
 */
const STORAGE_BLOCKED_MESSAGE =
  'This browser is blocking site data, so we can’t keep your invite while you sign in. '
  + 'Allow cookies and site data for this site, or open your invite link in another browser.';

/**
 * Shown when the bundle's auth adapter and the backend's provider disagree —
 * see the `adapterSkew` comment below. Deliberately about TIME rather than
 * about the user's browser: they did nothing wrong, and the fix is a deploy
 * finishing.
 */
const PROVIDER_SKEW_MESSAGE =
  'Sign-in is briefly unavailable while we finish updating Lutiq. Reload this page in a '
  + 'few minutes and your invite link will work.';

const ScalekitAcceptInvite = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const auth = useAuthOptional();
  const authenticated = auth?.authenticated ?? false;

  // URL first, stash second. The URL wins so a freshly-clicked invite always
  // beats a leftover token from an abandoned attempt in the same tab.
  const urlToken = searchParams.get('token');
  const token = useMemo(() => urlToken ?? readStashedToken(), [urlToken]);

  // Move the token out of the address bar as soon as there is a confirmed
  // second copy of it — see the deep-link-stash comment above for why the
  // clean `INVITE_RETURN_PATH` alone does not cover this. `replace` so the
  // dirty URL does not survive in the tab's history either.
  //
  // If the stash could not be confirmed, the URL stays exactly as it is: it is
  // then the only copy, and `token` above keeps resolving to it.
  useEffect(() => {
    if (!urlToken) return;
    if (!stashToken(urlToken)) return;
    const cleaned = new URLSearchParams(searchParams);
    cleaned.delete('token');
    setSearchParams(cleaned, { replace: true });
  }, [urlToken, searchParams, setSearchParams]);

  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error' | 'expired'>('idle');
  const [invite, setInvite] = useState<InviteStatus | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [storageBlocked, setStorageBlocked] = useState(false);

  // WHICH UI vs WHICH ADAPTER — the one skew this page can see and must not
  // walk into.
  //
  // `useAuthProviderName` picks the BRANCH from `auth_provider` — which
  // provider ADMINISTERS users, i.e. whether passwords exist at all.
  // `resolveAuthProviderName()` answers `login_provider` — which adapter
  // `auth.loginWithPassword` dispatches to (`authProvider.ts`). Since US-011
  // both come from the same runtime response, so the old build-time skew (one
  // deploy wide) is gone. THE GUARD IS NOT: the two are SEPARATE SWITCHES
  // (`IDENTITY_ADMIN_PROVIDER` vs `DASHBOARD_SCALEKIT_LOGIN_ENABLED`), and the
  // rollout deliberately moves lifecycle before advertisement — so a deployment
  // that administers users in Scalekit while browsers still sign in through
  // Keycloak is a SUPPORTED state, not a transient one.
  //
  // In that state this branch renders and would then send the visitor to
  // Keycloak. They
  // authenticate against the wrong issuer, come back — the backend still
  // ACCEPTS that token, US-007 keeps the Keycloak dashboard tenant live for
  // rollback — and the redemption then dies at the provider write, because
  // `redeemInviteForCaller` acts on the subject THIS REQUEST authenticated as
  // and a Keycloak subject has no account under the active Scalekit provider.
  // Not even the polite 409: an upstream failure, after a full hosted round
  // trip, that no invitee can act on and a fresh invite would reproduce
  // exactly.
  //
  // So the button is replaced by a sentence that is true. This costs a real
  // screen during the recommended (backend-first) deploy order, which is why it
  // says "reload in a few minutes" rather than anything terminal — and it
  // blocks nothing that would otherwise work, because no mismatched pairing
  // completes a redemption. Runtime adapter selection (US-011) shrank the
  // window; it did not remove the state, because the two switches are meant to
  // be independently flippable.
  //
  // The other passwordless surface that calls into the auth seam,
  // `PasswordlessRecoveryPage`, deliberately has NO equivalent guard: all it
  // wants is a session, and under this same skew the keycloak adapter still
  // delivers one the backend accepts. Redemption is the operation that needs
  // the caller's subject to exist under the ACTIVE provider, so redemption is
  // the operation that breaks.
  const adapterSkew = resolveAuthProviderName() !== 'scalekit';

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Invite token is missing. Open the link from your invite email again.');
      return;
    }
    setStatus('loading');
    void publicRequestJson<InviteStatus>(`/api/auth/invites/${encodeURIComponent(token)}`)
      .then((payload) => {
        setInvite(payload);
        setStatus(payload.state === 'expired' ? 'expired' : 'idle');
      })
      .catch((error) => {
        setStatus('error');
        setMessage(error instanceof Error ? error.message : 'Invite is invalid.');
      });
  }, [token]);

  const requiresName = invite?.actionType === 'invite';

  const handleSignIn = () => {
    // Usually a no-op rewrite of what the strip effect already stashed. It
    // matters in the one case where that effect declined to strip: storage was
    // blocked on arrival, so this is the retry — and its answer is a GATE.
    //
    // Redeeming needs a session, the session needs a full-page trip to the
    // hosted login, and the return lands on the clean `INVITE_RETURN_PATH`. So
    // with no stash there is no token on the far side: the visitor would spend
    // an entire round trip to arrive at "Invite token is missing", having lost
    // the URL copy they still have RIGHT NOW. Refusing to start is strictly
    // better than that dead end.
    //
    // The alternative — putting the token back on the return path only in this
    // case — was considered and rejected. It would hand the token to the
    // authorize endpoint, i.e. re-open the exact exposure the strip exists to
    // close, and it would not even work: `transientStateStorage` in
    // `auth/scalekit.ts` degrades sessionStorage → localStorage → memory, and a
    // browser refusing web storage outright leaves the PKCE verifier in memory,
    // which the navigation destroys. Interactive login is structurally
    // impossible there, token on the return path or not.
    //
    // Re-checked on every click rather than latched, so a visitor who allows
    // site data and clicks again proceeds without a reload.
    if (!stashToken(token)) {
      setStorageBlocked(true);
      return;
    }
    setStorageBlocked(false);
    if (!auth) {
      // No AuthProvider on the tree (isolated render). Nothing to log in with;
      // send the browser at the app root, which mounts one.
      window.location.href = '/';
      return;
    }
    // Provider-neutral hosted-auth entry. Under Scalekit this opens the hosted
    // sign-in page (Google + one-time email code); the method name is the
    // US-008 seam and does NOT mean "password login".
    //
    // The argument is a bare path on purpose — see INVITE_RETURN_PATH.
    void auth.loginWithPassword(INVITE_RETURN_PATH);
  };

  const handleRedeem = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus('loading');
    setMessage(null);
    try {
      await apiRequestJson('/api/auth/invites/redeem', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          ...(requiresName ? { firstName, lastName } : {}),
        }),
      });
      // Spent, or already spent by this same principal — the endpoint is
      // idempotent, so both land here. Drop the stash so a later visit to a
      // clean /accept-invite doesn't retry a token that is finished with.
      clearStashedToken();
      setStatus('success');
    } catch (error) {
      setStatus('error');
      // Verbatim. The server chose one sentence for every refusal reason; this
      // is not the layer that gets to elaborate on it.
      setMessage(error instanceof Error ? error.message : 'Unable to complete invite.');
    }
  };

  return (
    <AuthPageShell
      eyebrow="Account setup"
      title="Activate your account"
      subtitle={invite?.email ? `Continue as ${invite.email}` : undefined}
    >
      {status === 'loading' ? <LinearProgress /> : null}
      {message ? <Alert severity="error">{message}</Alert> : null}
      {status === 'expired' ? (
        <Alert severity="warning">{SCALEKIT_EXPIRED_MESSAGE}</Alert>
      ) : null}
      {status === 'success' ? (
        <>
          <Alert severity="success">
            You&rsquo;re all set. Your Lutiq account is ready.
          </Alert>
          <Button
            variant="contained"
            size="large"
            onClick={() => { window.location.href = '/'; }}
          >
            Go to dashboard
          </Button>
        </>
      ) : null}

      {/* Both action branches below require a LOADED invite, so a missing or
          invalid token shows its message and nothing else — never a dead form.
          And both are replaced by one sentence under `adapterSkew`: neither a
          sign-in that reaches the wrong issuer nor a redeem that would be
          refused for it is worth offering. */}
      {invite && status !== 'success' && status !== 'expired' && adapterSkew ? (
        <Alert severity="warning">{PROVIDER_SKEW_MESSAGE}</Alert>
      ) : null}

      {invite && status !== 'success' && status !== 'expired' && !adapterSkew
        && !authenticated ? (
        <>
          <Typography color="text.secondary">
            Lutiq accounts sign in with Google or a one-time email code — there is no
            password to set. Sign in and we&rsquo;ll finish setting up your access.
          </Typography>
          {storageBlocked ? (
            <Alert severity="warning">{STORAGE_BLOCKED_MESSAGE}</Alert>
          ) : null}
          <Button
            variant="contained"
            size="large"
            onClick={handleSignIn}
            disabled={status === 'loading'}
          >
            Sign in to accept
          </Button>
        </>
      ) : null}

      {invite && status !== 'success' && status !== 'expired' && !adapterSkew
        && authenticated ? (
        <Box component="form" onSubmit={handleRedeem} noValidate>
          <Stack spacing={2}>
            {requiresName ? (
              <>
                <TextField
                  label="First name"
                  fullWidth
                  value={firstName}
                  onChange={(event) => setFirstName(event.target.value)}
                  disabled={status === 'loading'}
                />
                <TextField
                  label="Last name"
                  fullWidth
                  value={lastName}
                  onChange={(event) => setLastName(event.target.value)}
                  disabled={status === 'loading'}
                />
              </>
            ) : null}
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={
                status === 'loading'
                || (requiresName
                  && (firstName.trim().length === 0 || lastName.trim().length === 0))
              }
            >
              Accept invite
            </Button>
          </Stack>
        </Box>
      ) : null}

      <Button variant="text" onClick={() => { window.location.href = '/'; }}>
        Back to sign in
      </Button>
    </AuthPageShell>
  );
};

/**
 * `/accept-invite`.
 *
 * The passwordless branch is required when EITHER runtime switch has moved to
 * Scalekit:
 *
 *   - `login_provider=scalekit`: the browser must use hosted Scalekit auth even
 *     while user administration still runs in Keycloak (the staging cutover
 *     state that exercises this route).
 *   - `auth_provider=scalekit`: passwords no longer exist, even if login is
 *     still temporarily advertised through Keycloak. `adapterSkew` keeps that
 *     supported transition read-only until the login switch catches up.
 *
 * Only the all-Keycloak state may render the password form. Treating
 * `auth_provider` as the sole router sent a live `login_provider=scalekit`
 * invite to the Keycloak password surface.
 */
const AcceptInvitePage = () => {
  const authProvider = useAuthProviderName();
  const loginProvider = useLoginProviderName();
  return authProvider === 'scalekit' || loginProvider === 'scalekit'
    ? <ScalekitAcceptInvite />
    : <KeycloakAcceptInvite />;
};

export default AcceptInvitePage;
